import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { useConfetti } from '@sparklayer-io/spark-dash-core/hooks/use-confetti';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { Accordion } from '@ui/themed';
import { useState } from 'react';
import { useToggleOnboardingFlag } from '../../hooks/use-toggle-onboarding-flag';
import { firstIncompleteStep, SECTION_TO_STEP } from '../../lib/onboarding-sections';
import { isOnboardingComplete, type OnboardingStepKey } from '../../lib/onboarding-steps';
import { ConnectStep } from './connect-step';
import { FrontendStep } from './frontend-step';
import { LaunchStep } from './launch-step';
import { SettingsStep } from './settings-step';
import { SyncStep } from './sync-step';

/** The `/toggle-onboarding-flag/:flag` slug for each step that has a manual complete/undo action. */
const FLAG_BY_STEP: Partial<Record<OnboardingStepKey, string>> = {
  onboarding_frontend: 'frontend',
  onboarding_sync: 'sync',
  onboarding_settings: 'settings',
  onboarding_launch: 'launch',
};

export function Onboarding() {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const confetti = useConfetti();
  const toggleFlag = useToggleOnboardingFlag();

  const [expanded, setExpanded] = useState<Partial<Record<OnboardingStepKey, boolean>>>(() => {
    const query = navigationHandler.query();
    const requestedStep = query.expand ? SECTION_TO_STEP[query.expand] : undefined;
    const stepToOpen =
      requestedStep ?? (isOnboardingComplete(env) ? null : firstIncompleteStep(env));
    return stepToOpen ? { [stepToOpen]: true } : {};
  });
  // Preticks the pricing checklist - the price-list assign flow deep-links here already done.
  const [precheckPricing] = useState<boolean>(() => !!navigationHandler.query().precheckPricing);
  const [pendingKey, setPendingKey] = useState<OnboardingStepKey | null>(null);

  function setStepExpanded(key: OnboardingStepKey, value: boolean) {
    setExpanded((prev) => ({ ...prev, [key]: value }));
  }

  function handleToggle(key: OnboardingStepKey) {
    const flag = FLAG_BY_STEP[key];
    if (!flag) return;

    setPendingKey(key);
    toggleFlag.mutate(
      { flag, key },
      {
        onSuccess: (freshEnv) => {
          if (!freshEnv[key]) return;

          if (isOnboardingComplete(freshEnv)) confetti.finale();
          else confetti.celebrate();

          // The step that was just completed sits open (the button lives in its body) - close it,
          // then open whichever one is next so the user can carry straight on.
          setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
          const next = firstIncompleteStep(freshEnv);
          if (next) setStepExpanded(next, true);
        },
        onSettled: () => setPendingKey((current) => (current === key ? null : current)),
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Accordion>
        <ConnectStep
          isExpanded={!!expanded.onboarding_platform}
          onExpandedChange={(value) => setStepExpanded('onboarding_platform', value)}
        />
        {env.connected_platform !== 'wix' && (
          <FrontendStep
            isExpanded={!!expanded.onboarding_frontend}
            onExpandedChange={(value) => setStepExpanded('onboarding_frontend', value)}
            loading={pendingKey === 'onboarding_frontend'}
            onToggle={() => handleToggle('onboarding_frontend')}
          />
        )}
        <SyncStep
          isExpanded={!!expanded.onboarding_sync}
          onExpandedChange={(value) => setStepExpanded('onboarding_sync', value)}
          loading={pendingKey === 'onboarding_sync'}
          onToggle={() => handleToggle('onboarding_sync')}
          initialPrecheck={precheckPricing}
        />
        <SettingsStep
          isExpanded={!!expanded.onboarding_settings}
          onExpandedChange={(value) => setStepExpanded('onboarding_settings', value)}
          loading={pendingKey === 'onboarding_settings'}
          onToggle={() => handleToggle('onboarding_settings')}
        />
        <LaunchStep
          isExpanded={!!expanded.onboarding_launch}
          onExpandedChange={(value) => setStepExpanded('onboarding_launch', value)}
          loading={pendingKey === 'onboarding_launch'}
          onToggle={() => handleToggle('onboarding_launch')}
        />
      </Accordion>
    </div>
  );
}
